"use client"
import { kineticLog } from "@/app/dashboard/builder/[templateId]/client/kineticLog";
import { setup_steps } from "@/types/db";
import { createClient } from "@/utils/supabase/client";

export const markSetupStepComplete = async (step: keyof setup_steps, companyId: string) => {
    const supabase = createClient();
    try {

        kineticLog("step✅",step);
        kineticLog("companyId✅",companyId);
        // Fetch the current setup_steps
        const { data: companyUser, error: companyUserError } = await supabase
            .from("company")
            .select("setup_steps")
            .eq("id", companyId)
            .single();

        if (companyUserError) {
            console.error(`Error fetching company data:`, companyUserError);
            throw new Error(`Failed to fetch company data`);
        }

        let newSetupSteps;
        if (companyUser && companyUser.setup_steps) {
            // Merge the new step with existing steps
            newSetupSteps = { ...companyUser.setup_steps, [step]: true };
        } else {
            // Create new setup_steps object if it doesn't exist
            newSetupSteps = { [step]: true };
        }

        // Update the company row
        console.log('[markSetupStepComplete] Updating company with:', { companyId, step, newSetupSteps });
        const { data: updateResult, error: updateError } = await supabase
            .from("company")
            .update({ setup_steps: newSetupSteps })
            .eq("id", companyId)
            .select();

        console.log('[markSetupStepComplete] Update result:', { updateResult, updateError });

            return{
                success: true,
                message: `Step ${step} completed for company ${companyId}`,
                error: updateError
            }

    } catch (err) {
        console.error(`Error in markSetupStepComplete for ${step}:`, err);
        throw new Error(`An unexpected error occurred updating setup step: ${step}`);
    }
};

export const checkSetupStepComplete = async (step: keyof setup_steps, companyId: string) => {
    kineticLog("step✅",step);
    kineticLog("companyId✅",companyId);
    console.log('[checkSetupStepComplete] Checking:', { step, companyId });
    const supabase = createClient();
    const { data: companyUser, error: companyUserError } = await supabase
        .from("company")
        .select("setup_steps")
        .eq("id", companyId)
        .single();

    console.log('[checkSetupStepComplete] Query result:', { companyUser, companyUserError });
    kineticLog("companyUser✅",companyUser);

    if (companyUserError) {
        console.error(`Error fetching company data:`, companyUserError);
        return false;
    }

    // Check if step exists AND is true (completed)
    const isComplete = companyUser && companyUser.setup_steps && (companyUser.setup_steps[step] === true || companyUser.setup_steps[step] === "completed");
    console.log('[checkSetupStepComplete] Result:', { step, isComplete, setupSteps: companyUser?.setup_steps });

    if (isComplete) {
        return true;
    }
    return false;
};

export const checkSetupStepExists = async (step: keyof setup_steps, companyId: string) => {
    const supabase = createClient();
    const { data: companyUser, error: companyUserError } = await supabase
        .from("company")
        .select("setup_steps")
        .eq("id", companyId)
        .single();

    if (companyUserError) {
        console.error(`Error fetching company data:`, companyUserError);
        return false;
    }

    // Check if step exists (regardless of its value)
    if (companyUser && companyUser.setup_steps && step in companyUser.setup_steps) {
        return true;
    }
    return false;
};

export const markSetupStepPending = async (step: keyof setup_steps, companyId: string) => {
    const supabase = createClient();
    try {
        // Fetch the current setup_steps
        const { data: companyUser, error: companyUserError } = await supabase
            .from("company")
            .select("setup_steps")
            .eq("id", companyId)
            .single();

        if (companyUserError) {
            console.error(`Error fetching company data:`, companyUserError);
            throw new Error(`Failed to fetch company data`);
        }

        let newSetupSteps;
        if (companyUser && companyUser.setup_steps) {
            // Merge the new step with existing steps, marking it as "pending"
            newSetupSteps = { ...companyUser.setup_steps, [step]: "pending" };
        } else {
            // Create new setup_steps object if it doesn't exist
            newSetupSteps = { [step]: "pending" };
        }

        // Update the company row
        const { error: updateError } = await supabase
            .from("company")
            .update({ setup_steps: newSetupSteps })
            .eq("id", companyId);

        return {
            success: true,
            message: `Step ${step} marked as pending for company ${companyId}`,
            error: updateError
        }

    } catch (err) {
        console.error(`Error in markSetupStepPending for ${step}:`, err);
        throw new Error(`An unexpected error occurred updating setup step: ${step}`);
    }
};

export const checkSetupStepPending = async (step: keyof setup_steps, companyId: string) => {
    const supabase = createClient();
    const { data: companyUser, error: companyUserError } = await supabase
        .from("company")
        .select("setup_steps")
        .eq("id", companyId)
        .single();

    if (companyUserError) {
        console.error(`Error fetching company data:`, companyUserError);
        return false;
    }

    // Check if step exists AND is "pending"
    if (companyUser && companyUser.setup_steps && companyUser.setup_steps[step] === "pending") {
        return true;
    }
    return false;
};


export const isSetupCompleted = async (companyId: string) => {
    kineticLog("companyId", companyId);
    const supabase = createClient();
    const { data: companyUser, error: companyUserError } = await supabase
        .from("company")
        .select("setup_steps")
        .eq("id", companyId)
        .single();

    kineticLog("companyUser", companyUser);

    if (companyUserError) {
        console.error(`Error fetching company data:`, companyUserError);
        kineticLog("companyUserError", companyUserError);
        return {
            isSetupCompleted: false,
            setupSteps: null
        };
    }

    // Check if company exists and has setup_steps
    if (!companyUser || !companyUser.setup_steps) {
        return {
            isSetupCompleted: false,
            setupSteps: null
        };
    }

    const setupSteps = companyUser.setup_steps;

    const isSetupCompleted = setupSteps.setup === "completed";

    // Check if setup_completed is marked as "completed"
    return {
        isSetupCompleted: isSetupCompleted,
        setupSteps: setupSteps
    }
};

export const isMockDataVisible = async (companyId: string) => {
    const isShopifySetupCompleted = await checkSetupStepComplete("shopify", companyId);
    return isShopifySetupCompleted;
}

export const markSetupStepIncomplete = async (step: keyof setup_steps, companyId: string) => {
    const supabase = createClient();
    try {
        // Fetch the current setup_steps
        const { data: companyUser, error: companyUserError } = await supabase
            .from("company")
            .select("setup_steps")
            .eq("id", companyId)
            .single();

        if (companyUserError) {
            console.error(`Error fetching company data:`, companyUserError);
            throw new Error(`Failed to fetch company data`);
        }

        let newSetupSteps;
        if (companyUser && companyUser.setup_steps) {
            // Merge the new step with existing steps, marking it as false (incomplete)
            newSetupSteps = { ...companyUser.setup_steps, [step]: false };
        } else {
            // Create new setup_steps object if it doesn't exist
            newSetupSteps = { [step]: false };
        }

        // Update the company row
        console.log('[markSetupStepIncomplete] Updating company with:', { companyId, step, newSetupSteps });
        const { data: updateResult, error: updateError } = await supabase
            .from("company")
            .update({ setup_steps: newSetupSteps })
            .eq("id", companyId)
            .select();

        console.log('[markSetupStepIncomplete] Update result:', { updateResult, updateError });

        return {
            success: true,
            message: `Step ${step} marked as incomplete for company ${companyId}`,
            error: updateError
        }

    } catch (err) {
        console.error(`Error in markSetupStepIncomplete for ${step}:`, err);
        throw new Error(`An unexpected error occurred updating setup step: ${step}`);
    }
};



